'use client'

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '../components/Input';
import Button from '../components/Button';
import Spinner from "../components/Spinner";
import { useToaster } from '../context/ToasterContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConnectors } from '../redux/globalDataSlice';
import api from '../api/axios';

const makeSchema = (isEditMode) => yup.object().shape({
  connectorName: yup.string().required('Connector name is required'),
  connectorType: yup.string().required('Please select connector type'),
  username: yup.string().required('Username is required'),
  password: yup.string().when(['connectorType', 'connectorApi'], {
    is: (type, api) => type === 'VONAGE' && api === 'SMS',
    then: (s) => s.required('Password is required'),
    otherwise: (s) => s.notRequired()
  }),
  connectorApi: yup.string().when('connectorType', {
    is: 'VONAGE',
    then: (s) => s.required('Please select Vonage API type'),
    otherwise: (s) => s.notRequired()
  }),
  applicationId: yup.string().when(['connectorType', 'connectorApi'], {
    is: (type, api) => type === 'VONAGE' && api === 'MESSAGES',
    then: (s) => s.required('Application ID is required'),
    otherwise: (s) => s.notRequired()
  }),
  privateKeyPem: yup.string().when(['connectorType', 'connectorApi'], {
    is: (type, api) => type === 'VONAGE' && api === 'MESSAGES',
    then: (s) => isEditMode ? s.notRequired() : s.required('Private key is required'),
    otherwise: (s) => s.notRequired()
  }),
});

const typeOptions = [
  { value: 'VONAGE', label: 'Vonage' },
  { value: 'TWILIO', label: 'Twilio' },
];

const pages = [{ name: 'Connectors', href: '/connectors' }];

export default function AddEditConnector() {
  const { id } = useParams();
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { toaster } = useToaster();
  const dispatch = useDispatch();

  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);

  const [loading, setLoading] = useState(true);
  const [connectorData, setConnectorData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(makeSchema(isEditMode)),
    defaultValues: {
      connectorName: '',
      connectorType: '',
      username: '',
      password: '',
      connectorApi: 'SMS',
      applicationId: '',
      privateKeyPem: '',
    },
  });

  const connectorType = watch("connectorType");
  const connectorApi = watch("connectorApi");

  useEffect(() => {
    if (!isEditMode && activeAccountId) {
      setValue('accountId', activeAccountId, { shouldDirty: false });      
    }
  }, [isEditMode, activeAccountId, setValue]);

  useEffect(() => {
    const fetchConnectorData = async () => {
      try {
        const response = await api.get(
          `${process.env.REACT_APP_BASE_API_URL}/mvapi/connectors/account/${activeAccountId}/${id}`
        );
        setConnectorData(response.data);
      } catch (error) {
        console.error('Fetch connector error:', error);
        toaster.error('Failed to load connector: ' + error);
      } finally {
        setLoading(false);
      }
    };

    if (isEditMode) {
      fetchConnectorData();
    } else {
      setLoading(false);
      console.log("AccountId: ", activeAccountId);
    }
  }, [id, isEditMode, toaster,activeAccountId ]);

  useEffect(() => {
    if (isEditMode && connectorData) {
      const filled = {
        ...connectorData,
        accountId: connectorData.accountId || activeAccountId || '',
      };
      reset(filled);
    }
  }, [connectorData, isEditMode, reset, activeAccountId]);

  const onSubmit = async (data) => {
    try {
      console.log("Submitting with accountId:", activeAccountId);
      const accountId = data.accountId || activeAccountId;
      if (!accountId) {
        toaster.error('No account selected. Please choose an account first.');
        return;
      }

      const requestData = { ...data, accountId };

      if (isEditMode) {
        requestData.id = id;
        await api.put(
          `${process.env.REACT_APP_BASE_API_URL}/mvapi/connectors/account/${activeAccountId}/${id}`,
          requestData
        );
        toaster.success('Connector updated successfully!');
      } else {
        await api.post(
          `${process.env.REACT_APP_BASE_API_URL}/mvapi/connectors/account/${activeAccountId}`,
          requestData
        );
        toaster.success('Connector created successfully!');
      }

      // Refresh the connectors list for the CURRENT account
      await dispatch(fetchConnectors(accountId));

      navigate('/connectors');
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 422 && error.response?.data?.error) {
        toaster.error(error.response.data.error);
      } else {
        toaster.error('Something went wrong. Please try again.');
      }
    }
  };

  const waitingForAccount = !isEditMode && !activeAccountId;

  return (
    <>
      {(loading || waitingForAccount) && <Spinner />}

      {!loading && !waitingForAccount && (
        <div className="px-4 sm:px-6 lg:px-8">
          <Breadcrumbs pages={pages} />

          <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
            {isEditMode ? 'Edit connector' : 'Create a connector'}
          </h2>
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {isEditMode ? 'Update the connector details below.' : 'Create or edit a connector here'}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="space-y-12">
              <div className="border-b border-gray-200 dark:border-gray-800 pb-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                  {/* Hidden field to carry accountId with submission */}
                  <input type="hidden" {...register('accountId')} value={isEditMode ? connectorData?.accountId : activeAccountId || ''} readOnly />

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      label="Connector name"
                      placeholder="e.g. MyCompany"
                      required
                      {...register('connectorName')}
                      error={errors.connectorName?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      as="select"
                      required
                      label="Connector type"
                      options={typeOptions}
                      {...register('connectorType')}
                      error={errors.connectorType?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      required
                      label="Username"
                      placeholder="Username"
                      {...register('username')}
                      error={errors.username?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      type="password"
                      required={connectorType === 'VONAGE' && connectorApi === 'SMS'}
                      label="Password"
                      placeholder="Password"
                      {...register('password')}
                      error={errors.password?.message}
                    />
                  </div>

                  {connectorType === 'VONAGE' && (
                    <>
                      <div className="col-span-2">
                        <div className="flex flex-col gap-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Vonage API *</p>
                          <Input
                            id="connectorApi_sms"
                            label="SMS"
                            as="radio"
                            value="SMS"
                            {...register('connectorApi')}
                            error={errors.connectorApi?.message}
                          />
                          <Input
                            id="connectorApi_messages"
                            label="Messages"
                            as="radio"
                            value="MESSAGES"
                            {...register('connectorApi')}
                            error={errors.connectorApi?.message}
                          />
                          {errors.connectorApi && (
                            <p className="text-xs text-red-600 dark:text-red-500">{errors.connectorApi.message}</p>
                          )}
                        </div>
                      </div>

                      {connectorApi === 'MESSAGES' && (
                        <>
                          <div className="col-span-2 sm:col-span-1">
                            <Input
                              required
                              label="Application ID"
                              placeholder="Application ID"
                              {...register('applicationId')}
                              error={errors.applicationId?.message}
                            />
                          </div>

                          <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Private Key {!isEditMode && <span className="text-red-500">*</span>}
                            {isEditMode && <span className="text-gray-400 font-normal"> (leave blank to keep existing)</span>}
                          </label>
                          <textarea
                            rows={12}
                            placeholder="-----BEGIN RSA PRIVATE KEY-----"
                            {...register('privateKeyPem')}
                            className="w-full font-mono text-xs rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-y"
                          />
                          {errors.privateKeyPem && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-500">{errors.privateKeyPem.message}</p>
                          )}
                        </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-x-6">
              <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate('/connectors')}
                    disabled={isSubmitting}
                  >
                    Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting
                  ? (isEditMode ? 'Updating...' : 'Saving...')
                  : (isEditMode ? 'Update details' : 'Save details')}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}